import React, { useCallback } from "react";

import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { Grid } from "@material-ui/core";
import { Form, getIn, useFormikContext } from "formik";
import { useDispatch, useSelector } from "react-redux";

import { promptDeleteConsignmentFromModal } from "actions/modals/consignment";

import { SubtleBadge } from "components/Badge";
import IconTextButton from "components/Button/IconTextButton";
import { VendorCommissionSection } from "components/ConsignmentForm/VendorCommissionSection";
import { LegacyVendorSplitSection } from "components/ConsignmentForm/LegacyVendorSplitsSection";
import { VendorSplitSection } from "components/ConsignmentForm/VendorSplitSection";
import { FeesAndChargesCollapse } from "components/FeesAndChargesCollapse";
import { CollapseLabel, FormCollapse } from "components/Form";
import { useSubmitHandler } from "components/Form/FormikControls";
import { MediaCollapse } from "components/Media/MediaCollapse";

import { ConsignmentModalSection } from "constants/navigation";
import { ConsignmentPermissions } from "constants/permissions";
import { SaleType } from "constants/sale";

import {
  ForClearingSale,
  ForNotClearingSale,
  ForSaleType,
} from "containers/ForSaleType";
import { ForLivestockAgent } from "containers/ForUserType";

import {
  getLivestockSaleId,
  openConsignmentCarrierChargeModal,
} from "lib/navigation";
import { hasPermission } from "lib/permissions";

import {
  getBusinessById,
  getConsignmentById,
  getDeploymentSaleIdByLivestockSaleIdAndAgencyId,
  getIsLivestockAgent,
  getIsSaleyardAdmin,
  getPrimaryAgency,
  getSaleLotIdsByConsignmentId,
  getSaleLotQuantityByConsignmentId,
  getSaleyardScanSaleLotByConsignmentId,
  getScansBySaleLotId,
  getVendorSplitConsignmentIdsByParentConsignmentId,
  selectDeploymentCodeByAgencyIdLookup,
  selectDeploymentCodeByConsignmentIdLookup,
} from "selectors";

import {
  useHasVendorSplitPermission,
  useHasVendorCommissionPermission,
} from "hooks";

import AdvancedDraftingSection from "./AdvancedDrafting";
import {
  CarrierChargeSection,
  CarrierChargeSectionHeader,
} from "./CarrierChargeSection";
import ClearingSalePickupDetails from "./ClearingSalePickupDetails";
import { DraftingForm } from "./DraftingForm";
import EIDsSection from "./EIDsSection";
import { GeneralForm, GeneralFormSectionHeader } from "./GeneralForm";
import { NLISCollapse } from "./nlis";
import NotesSection from "./NotesSection";
import { SaleLotSection } from "./SaleLotSection";

const getDeploymentSaleIdForCreateOrEditConsignment =
  (consignmentId, livestockSaleId, agencyId) => state => {
    // When editing an existing consignment, a Consignment Id is provided, we must honour the Deployment Sale attached to that
    // NOTE: This only accounts for editing a Consignment from THE CURRENT Livestock Sale.
    if (consignmentId) {
      return getConsignmentById(consignmentId)(state)?.deployment_sale;
    }
    const isLivestockAgent = getIsLivestockAgent(state);
    const isSaleyardAdmin = getIsSaleyardAdmin(state);

    // When consigning to another Deployment/Livestock Sale, a Livestock Sale Id is provided, and it will NOT be the current Livestock Sale Id
    if (isSaleyardAdmin) {
      // When **creating** a new Consignment as a Saleyard Admin, determine the Deployment Sale Id by the selected Agency Id, and the selected Livestock Sale Id
      return getDeploymentSaleIdByLivestockSaleIdAndAgencyId(
        livestockSaleId,
        agencyId,
      )(state);
    } else if (isLivestockAgent) {
      const primaryAgency = getPrimaryAgency(state);
      // When **creating** a new Consignment as a Livestock Agent, determine the Deployment Sale Id by the user's default Agency Id, and the selected Livestock Sale Id
      return getDeploymentSaleIdByLivestockSaleIdAndAgencyId(
        livestockSaleId,
        primaryAgency.agency_id,
      )(state);
    }
  };

export const ConsignmentFormBody = ({
  associatedCarrierChargesCount,
  carrierName,
  changeSection,
  consignmentId,
  consignmentsByVendorNumber,
  deploymentSaleId,
  isCreate,
  livestockSaleId = getLivestockSaleId(), // defaults to the current Livestock Sale Id
  readOnly,
  requireConsignedFrom,
  restrictConsignedFrom,
  setIsSubmitEnabled,
  selectedSaleType = null,
  saleyardName,
  section,
  showSaleLots,
  showClearingSection = true,
  showNominations = true,
}) => {
  const { values, setFieldValue } = useFormikContext();
  const dispatch = useDispatch();

  useSubmitHandler(isCreate, setIsSubmitEnabled);

  const saleyardScanSaleLot =
    useSelector(getSaleyardScanSaleLotByConsignmentId(values.id)) || {};

  const deploymentCodeByConsignmentIdLookup = useSelector(
    selectDeploymentCodeByConsignmentIdLookup,
  );

  const saleLotIds =
    useSelector(getSaleLotIdsByConsignmentId(consignmentId)) || [];
  const consignmentSaleLotsHeadCount = useSelector(
    getSaleLotQuantityByConsignmentId(consignmentId),
  );

  const saleyardScans =
    useSelector(getScansBySaleLotId(saleyardScanSaleLot.id)) || [];

  const deploymentCodeByAgencyIdLookup = useSelector(
    selectDeploymentCodeByAgencyIdLookup,
  );

  const isSaleyardAdmin = useSelector(getIsSaleyardAdmin);
  const { id, vendorNumber, agency_id } = values;
  const consignmentsWithSameVendorNumber =
    consignmentsByVendorNumber && consignmentsByVendorNumber[vendorNumber]
      ? consignmentsByVendorNumber[vendorNumber].filter(c => {
          if (!id && agency_id) {
            // if a new consignment, and an agency is selected (ie: SY admin)
            return (
              deploymentCodeByAgencyIdLookup[agency_id] ===
              deploymentCodeByConsignmentIdLookup[c.id]
            );
          } else if (!id && !isSaleyardAdmin) {
            // if a new consignment - no agency has been selected and is not a saleyard admin
            return c.vendorNumber === vendorNumber;
          } else {
            // otherwise an existing consignment
            // if a SY admin - The deploymentCode is important : if a stock agent it will be identical (check it anyway)
            return (
              c.id !== id &&
              deploymentCodeByConsignmentIdLookup[c.id] ===
                deploymentCodeByConsignmentIdLookup[id]
            );
          }
        })
      : [];

  const agencyId = getIn(values, "agency_id");
  const vendorId = getIn(values, "vendor_id");

  const headcount = values?.quantity;
  const vendorName = useSelector(getBusinessById(vendorId))?.name || "";
  const invoiceAmount = values?.carrierCharge?.invoiceAmount;
  const NVD = values?.NVD;

  const consignmentHasSaleLots = saleLotIds.length > 0;

  // Build a toggle function for each section.
  const toggleSectionMap = React.useMemo(() => {
    return Object.keys(ConsignmentModalSection).reduce((acc, sectionName) => {
      acc[sectionName] = e => {
        changeSection && changeSection(sectionName);
        e.stopPropagation();
      };
      return acc;
    }, {});
  }, [changeSection]);

  const isSectionOpen = testSection => section === testSection;

  function onResetCarrierCharge() {
    setFieldValue("carrierChargeId", null);
    setFieldValue("carrierCharge", {});
  }

  function onFindCarrierCharge() {
    openConsignmentCarrierChargeModal(
      consignmentId,
      false,
      window.location.hash,
    );
  }

  const generalFormHeader = (
    <GeneralFormSectionHeader
      headcount={headcount}
      vendorName={vendorName}
      NVD={NVD}
    />
  );
  const carrierFormHeader = (
    <CarrierChargeSectionHeader
      carrierName={carrierName}
      invoiceAmount={invoiceAmount}
    />
  );
  const header = (
    <Grid data-tour="saleLotsTab" container>
      <SubtleBadge>Sale Lots</SubtleBadge>
      <CollapseLabel>
        {`${saleLotIds.length} Lot(s), ${consignmentSaleLotsHeadCount} Head`}
      </CollapseLabel>
    </Grid>
  );

  const calculateDeploymentSaleId = useSelector(
    getDeploymentSaleIdForCreateOrEditConsignment(
      consignmentId,
      livestockSaleId,
      agencyId,
    ),
  );

  // Manual vendor number is only allowed when consigning to the current Livestock Sale
  const isVendorNumberingAllowed = livestockSaleId === getLivestockSaleId();

  const hasVendorSplitsPermission = useHasVendorSplitPermission();

  const hasVendorCommissionPermission = useHasVendorCommissionPermission();

  const hasLegacyVendorSplits = useSelector(
    getVendorSplitConsignmentIdsByParentConsignmentId(consignmentId),
  );

  const hasDeletePermission = hasPermission(
    values,
    ConsignmentPermissions.delete,
  );

  const onClickDelete = useCallback(() => {
    dispatch(promptDeleteConsignmentFromModal(consignmentId));
  }, [consignmentId, dispatch]);

  return (
    <Form>
      <FormCollapse
        isOpen={isSectionOpen(ConsignmentModalSection.GENERAL)}
        onToggle={toggleSectionMap[ConsignmentModalSection.GENERAL]}
        header={generalFormHeader}
        dataTour={`collapse.${ConsignmentModalSection.GENERAL}`}
      >
        <GeneralForm
          consignmentId={consignmentId}
          deploymentSaleId={deploymentSaleId || calculateDeploymentSaleId}
          isCreate={isCreate}
          isVendorNumberingAllowed={isVendorNumberingAllowed}
          vendorNumConflictCount={consignmentsWithSameVendorNumber.length}
          restrictConsignedFrom={restrictConsignedFrom}
          selectedSaleType={selectedSaleType}
          requireConsignedFrom={requireConsignedFrom}
          saleyardName={saleyardName}
          consignmentHasSaleLots={consignmentHasSaleLots}
          readOnly={readOnly}
          showNominations={showNominations}
        />
        {!isCreate && (
          <Grid item xs={12} sm={6}>
            <IconTextButton
              icon={faTrash}
              color="gray40"
              onClick={onClickDelete}
              disabled={saleLotIds.length > 0 || !hasDeletePermission}
              dataTour="deleteConsignment"
              tooltip={
                saleLotIds.length > 0
                  ? "Consignment cannot be deleted when it has sale lots."
                  : ""
              }
            >
              Delete Consignment
            </IconTextButton>
          </Grid>
        )}
      </FormCollapse>
      <ForClearingSale selectedSaleType={selectedSaleType}>
        {showClearingSection && (
          <ClearingSalePickupDetails
            isOpen={isSectionOpen(ConsignmentModalSection.PICKUP_DETAILS)}
            onToggle={toggleSectionMap[ConsignmentModalSection.PICKUP_DETAILS]}
            vendorId={vendorId}
            readOnly={readOnly}
          />
        )}
      </ForClearingSale>
      <ForClearingSale selectedSaleType={selectedSaleType}>
        {showClearingSection && (
          <FeesAndChargesCollapse
            isOpen={isSectionOpen(ConsignmentModalSection.FEES_AND_CHARGES)}
            onToggle={
              toggleSectionMap[ConsignmentModalSection.FEES_AND_CHARGES]
            }
            consignmentId={consignmentId}
            readOnly={readOnly}
          />
        )}
      </ForClearingSale>
      <ForSaleType saleType={[SaleType.OVER_HOOKS, SaleType.PADDOCK]}>
        {isCreate && (
          <FormCollapse
            isOpen={isSectionOpen(ConsignmentModalSection.DRAFTING)}
            onToggle={toggleSectionMap[ConsignmentModalSection.DRAFTING]}
            header={<SubtleBadge>Draft Information</SubtleBadge>}
          >
            <DraftingForm />
          </FormCollapse>
        )}
        <ForLivestockAgent>
          <FormCollapse
            isOpen={isSectionOpen(ConsignmentModalSection.CARRIER_CHARGE)}
            onToggle={toggleSectionMap[ConsignmentModalSection.CARRIER_CHARGE]}
            header={carrierFormHeader}
            dataTour={
              isSectionOpen(ConsignmentModalSection.CARRIER_CHARGE)
                ? "hideCarrier"
                : "showCarrier"
            }
          >
            <CarrierChargeSection
              associatedCount={associatedCarrierChargesCount}
              onFindCarrierCharge={onFindCarrierCharge}
              onResetCarrierCharge={onResetCarrierCharge}
              carrierChargeId={values.carrierChargeId}
              showLinkToExisting={Boolean(consignmentId)}
            />
          </FormCollapse>
        </ForLivestockAgent>
      </ForSaleType>
      {showSaleLots && (
        <SaleLotSection
          isOpen={isSectionOpen(ConsignmentModalSection.SALE_LOTS)}
          onToggle={toggleSectionMap[ConsignmentModalSection.SALE_LOTS]}
          header={header}
          consignmentId={consignmentId}
        />
      )}
      {!isCreate && (
        <ForNotClearingSale>
          <MediaCollapse
            isOpen={isSectionOpen(ConsignmentModalSection.MEDIA)}
            onToggle={toggleSectionMap[ConsignmentModalSection.MEDIA]}
            consignmentId={consignmentId}
            readOnly={readOnly}
            saleType={selectedSaleType}
          />
          <NLISCollapse
            isOpen={isSectionOpen(ConsignmentModalSection.NLIS)}
            onToggle={toggleSectionMap[ConsignmentModalSection.NLIS]}
            consignmentId={consignmentId}
          />
          <EIDsSection
            consignmentId={consignmentId}
            isOpen={isSectionOpen(ConsignmentModalSection.EIDS)}
            onToggle={toggleSectionMap[ConsignmentModalSection.EIDS]}
            scans={saleyardScans}
          />
          <AdvancedDraftingSection
            isOpen={isSectionOpen(ConsignmentModalSection.ADVANCED_DRAFTING)}
            onToggle={
              toggleSectionMap[ConsignmentModalSection.ADVANCED_DRAFTING]
            }
            consignmentId={consignmentId}
            readOnly={readOnly}
          />
          <NotesSection
            isOpen={isSectionOpen(ConsignmentModalSection.NOTES)}
            onToggle={toggleSectionMap[ConsignmentModalSection.NOTES]}
            readOnly={readOnly}
          />
          {hasVendorSplitsPermission && hasLegacyVendorSplits.length > 0 && (
            <LegacyVendorSplitSection
              isOpen={isSectionOpen(
                ConsignmentModalSection.LEGACY_VENDOR_SPLITS,
              )}
              onToggle={
                toggleSectionMap[ConsignmentModalSection.LEGACY_VENDOR_SPLITS]
              }
              consignmentId={consignmentId}
            />
          )}
          {hasVendorSplitsPermission && (
            <VendorSplitSection
              isOpen={isSectionOpen(ConsignmentModalSection.VENDOR_SPLITS)}
              onToggle={toggleSectionMap[ConsignmentModalSection.VENDOR_SPLITS]}
              consignmentId={consignmentId}
            />
          )}
        </ForNotClearingSale>
      )}

      {hasVendorCommissionPermission && (
        <VendorCommissionSection
          isOpen={isSectionOpen(ConsignmentModalSection.VENDOR_COMMISSION)}
          onToggle={toggleSectionMap[ConsignmentModalSection.VENDOR_COMMISSION]}
          readOnly={readOnly}
        />
      )}
    </Form>
  );
};
